import { useQuery, queryOptions } from '@tanstack/react-query'
import { type QueryOptions } from '@/features/shared/utils/dataFetching/types'
import { fetchOptOutRestrictions } from '@/features/account/services/DataPrivacy/fetchers'
import { useGlobalDefaultShoppingAddress } from '@/features/account/services/Addresses/hooks'
import { useSession } from '@/features/authentication/utils/authentication/hooks/useSession'
import { useUser } from '@/features/account/services/User/hooks'
import { isOptOutCookieOrSignalActive } from '@/features/account/services/DataPrivacy/utils'
import { useZip } from '@/features/account/services/Zip/hooks'

type OptoutRestrictionsParams = { state?: string; zip_code?: string }

export type OptoutRestrictionsQueryKey = ReturnType<typeof getOptoutRestrictionsQueryKey>

const getOptoutRestrictionsQueryKey = (params: OptoutRestrictionsParams) =>
  ['opt-out-restrictions', params] as const

export const restrictionsQueryOptions = (params: OptoutRestrictionsParams) =>
  queryOptions({
    queryKey: getOptoutRestrictionsQueryKey(params),
    queryFn: fetchOptOutRestrictions,
  })

export const useQueryOptOutRestrictions = (options?: QueryOptions) => {
  const { state, street1 } = useGlobalDefaultShoppingAddress()
  const { isSessionValid } = useSession()
  const zip = useZip()
  const user = useUser()
  const userHasOptedOut = user?.do_not_share_sell_ta || isOptOutCookieOrSignalActive()

  const shouldFetchRestrictions = isSessionValid
    ? Boolean(user?.id && !user.default_shopping_address_id)
    : !street1

  return useQuery({
    ...restrictionsQueryOptions({ state, zip_code: zip }),
    ...options,
    enabled: !userHasOptedOut && shouldFetchRestrictions,
  })
}
